import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { camelCase } from 'lodash';

import PopupDialog from '../../../shared/PopupDialog';

import {
  fetchRunDetail,
  IRunDetail,
  RUN_DETAIL_ERROR_MESSAGE,
} from './runHistoryApi';

const TITLE = 'Run Detail';

interface IRunDetailDialogProps {
  open: boolean;
  close: () => void;
  clientId: number;
  rowId: string | null;
}

// A result is only shown while its key is the current one, so a row change
// hides the previous row's data in the same render.
interface IResult {
  key: string;
  detail: IRunDetail | null;
  failed: boolean;
}

const Field: FunctionComponent<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    {children}
  </Box>
);

const Value: FunctionComponent<{ value: string | number }> = ({ value }) => (
  <Typography variant="body2" sx={{ fontWeight: 500 }}>
    {String(value)}
  </Typography>
);

// Stored text is attacker-influenced: render it only as a React text node.
const RawText: FunctionComponent<{ text: string }> = ({ text }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 1,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      fontFamily: 'monospace',
      fontSize: '0.8125rem',
      bgcolor: 'action.hover',
      borderRadius: 1,
      maxHeight: 320,
      overflow: 'auto',
    }}
  >
    {text}
  </Box>
);

const DetailBody: FunctionComponent<{ detail: IRunDetail }> = ({ detail }) => {
  const isSubmission = detail.kind === 'submission';
  return (
    <>
      {isSubmission && detail.submissionId !== null && (
        <Field label="Submission ID">
          <Value value={detail.submissionId} />
        </Field>
      )}
      {!isSubmission && detail.triggerLogId !== null && (
        <Field label="Trigger log ID">
          <Value value={detail.triggerLogId} />
        </Field>
      )}
      {!isSubmission && detail.destinationSubmissionId !== null && (
        <Field label="Destination submission ID">
          <Value value={detail.destinationSubmissionId} />
        </Field>
      )}
      {isSubmission && detail.statusMessage !== null && (
        <Field label="Status message">
          <RawText text={detail.statusMessage} />
        </Field>
      )}
      {!isSubmission && detail.reasonCode !== null && (
        <Field label="Reason code">
          <Value value={detail.reasonCode} />
        </Field>
      )}
      {!isSubmission && detail.reasonDetail !== null && (
        <Field label="Reason detail">
          <RawText text={detail.reasonDetail} />
        </Field>
      )}
      {detail.truncated && (
        <Typography variant="body2" color="text.secondary">
          The text was truncated.
        </Typography>
      )}
      {detail.redacted && (
        <Typography variant="body2" color="text.secondary">
          Credential-like values were redacted.
        </Typography>
      )}
    </>
  );
};

const RunDetailDialog: FunctionComponent<IRunDetailDialogProps> = ({
  open,
  close,
  clientId,
  rowId,
}) => {
  const [result, setResult] = useState<IResult | null>(null);
  const key = open && rowId !== null ? `${clientId}|${rowId}` : null;

  useEffect(() => {
    if (key === null || rowId === null) return undefined;
    const controller = new AbortController();
    // The transport may ignore abort; a settled old request must not land.
    let active = true;
    fetchRunDetail(clientId, rowId, controller.signal).then(
      (detail) => {
        if (active) setResult({ key, detail, failed: false });
      },
      () => {
        // Any rejection after cleanup (including AbortError) is dropped here;
        // the error itself is never shown or logged.
        if (active) setResult({ key, detail: null, failed: true });
      }
    );
    return () => {
      active = false;
      controller.abort();
      // Reopening the same row must not show the previous result.
      setResult(null);
    };
  }, [key, clientId, rowId]);

  const current = result !== null && result.key === key ? result : null;

  let body: ReactNode;
  if (current?.failed) {
    body = (
      <Typography variant="body2" color="error">
        {RUN_DETAIL_ERROR_MESSAGE}
      </Typography>
    );
  } else if (current?.detail) {
    body = <DetailBody detail={current.detail} />;
  } else if (key !== null) {
    body = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} aria-label="Loading run detail" />
      </Box>
    );
  }

  return (
    <PopupDialog
      open={open}
      close={close}
      title={TITLE}
      maxWidth="md"
      closeButtonLabel="Close"
      primaryButtons={[]}
      // PopupDialog's default labelledby points at the title text, not its id.
      aria-labelledby={`${camelCase(TITLE)}-id`}
    >
      {body}
    </PopupDialog>
  );
};

export default RunDetailDialog;
